import React, { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { Button, Link, Text as ShrikeText } from '@perch-labs/shrike';
import { copyFor, routes } from 'config';
import { Text } from 'styles';
import {
  useFunnelRedirect,
  RedirectReason,
} from 'hooks/use-funnel-redirect.hook';
import { isInactive } from 'domain/plans';
import { ErrorPage } from 'components/errors';
import { LoadingPage } from 'components/loading';
import { trackEvent } from 'domain/tracking';
import {
  isNonVnmToVnmConnected,
  isVnmToVnm,
  isToNonVnmConnected,
  isNonVnmToVnmUnconnected,
  isToNonVnmUnconnected,
} from '../../moving.utils';
import { GET_SUCCESS_PLAN_QUERY } from './view-success.api';
import { commonMovingPayload } from '../../state-machine/actions';

const getCopy = copyFor('settings.moving.viewSuccess');
interface Copy {
  title: string;
  closedContent: string;
  content: string[];
  learnMoreText?: string;
  button: string;
}
interface Props {
  context: MovingStateMachineContext;
}
export const ViewSuccess = ({ context }: Props) => {
  const history = useHistory();
  const { utility, userId, newPlanId, selectedPlan, selectedUtilityAccount } =
    context;

  useEffect(() => {
    trackEvent('moving_move_successful', {
      metadata: commonMovingPayload(context),
    });
  }, []);

  const { data, loading, error } = useQuery<
    GetSuccessPlanQuery,
    GetSuccessPlanQueryVariables
  >(GET_SUCCESS_PLAN_QUERY, {
    skip: !newPlanId,
    variables: { id: newPlanId || -1 },
  });

  const [redirect, { loading: redirectLoading }] = useFunnelRedirect({
    idParams: {
      accountId: selectedUtilityAccount?.accountId,
      planId: newPlanId,
      utilityAccountId: data?.plan?.utilityAccounts[0]?.id,
    },
    userId,
    reason: RedirectReason.Moving,
  });

  let copy: Copy | undefined;
  const toNonVnmUnconnected = isToNonVnmUnconnected(context);
  if (isVnmToVnm(context)) {
    copy = getCopy('vnmToVnm');
  } else if (isNonVnmToVnmConnected(context)) {
    copy = getCopy('nonVnmToVnmConnected');
  } else if (isNonVnmToVnmUnconnected(context)) {
    copy = getCopy('nonVnmToVnmUnconnected');
  } else if (isToNonVnmConnected(context)) {
    copy = getCopy('toNonVnmConnected');
  } else if (toNonVnmUnconnected) {
    copy = getCopy('toNonVnmUnconnected');
  }

  if (loading) return <LoadingPage />;
  if (error || !copy || !data) {
    return <ErrorPage to={routes().settings} />;
  }

  if (selectedPlan && isInactive(selectedPlan.status)) {
    copy.content[0] = copy.closedContent;
  }

  const primaryButton = (
    <Button
      fullWidth={true}
      margin={{ bottom: '16px', top: '24px' }}
      onClick={() => {
        localStorage.setItem('nonVNMtoVNMMoveRequest', 'false');
        redirect();
      }}
      loading={redirectLoading}
    >
      {copy.button}
    </Button>
  );
  const secondaryButton = (
    <Button
      backgroundless
      fullWidth
      onClick={() => {
        localStorage.setItem('nonVNMtoVNMMoveRequest', 'false');
        history.push(routes().dashboard);
      }}
    >
      {getCopy('viewDashboardButton')}
    </Button>
  );
  const learnMore = copy.learnMoreText ? (
    <Link
      href="https://www.arcadia.com/article/what-is-community-solar"
      type="standalone"
    >
      {copy.learnMoreText}
    </Link>
  ) : null;

  return (
    <>
      <ShrikeText
        tag="h1"
        textStyle="heading800"
        margin={{ bottom: '16px', top: '33px' }}
      >
        {copy.title}
      </ShrikeText>
      {copy.content.map((item: string, index: number) => (
        <Text key={item} margin={{ bottom: '16px' }}>
          {item}
          {index === copy!.content.length - 1 && learnMore}
        </Text>
      ))}
      {!toNonVnmUnconnected && primaryButton}
      {!utility.vnmEligible && secondaryButton}
    </>
  );
};
